import { isMapAdditiveSelectionEvent } from '@/features/panel-planning/utils/panelMapSelection';

/**
 * 맵 싱글클릭(String 선택) · 더블클릭(Module 선택) 플러그인
 *
 * ctx:
 *   isSelectionBlocked?: () => boolean
 *   getSuppressMapSelectionUntil?: () => number
 *   isCtrlDragActive?: () => boolean
 *   getMapClickTargetFeatureAtPixel: (pixel) => feature|null
 *   onGroupSingleClick: (hit, { additiveKey }) => void
 *   onModuleDblClick: (hit) => void
 *   onClearSelection: () => void
 */
export const panelMapClickSelectionPlugin = {
  id: 'panel-map-click-selection',

  attach(host, ctx = {}) {
    const map = host.getMap();
    if (!map) {
      return;
    }

    let singleClickTimer = null;
    let ignoreSingleClickUntil = 0;

    const isClickSuppressed = () => {
      if (ctx.isSelectionBlocked?.()) {
        return true;
      }
      const until = ctx.getSuppressMapSelectionUntil?.() ?? 0;
      return Date.now() < until;
    };

    const onSingleClick = event => {
      if (isClickSuppressed()) {
        return;
      }
      if (ctx.isCtrlDragActive?.()) {
        return;
      }

      clearTimeout(singleClickTimer);
      singleClickTimer = setTimeout(() => {
        if (Date.now() < ignoreSingleClickUntil) {
          return;
        }
        if (isClickSuppressed()) {
          return;
        }
        const additiveKey = isMapAdditiveSelectionEvent(event);
        const pixel = map.getEventPixel(event.originalEvent);
        const hit = ctx.getMapClickTargetFeatureAtPixel?.(pixel);
        if (!hit) {
          if (!additiveKey) {
            ctx.onClearSelection?.();
          }
          return;
        }
        ctx.onGroupSingleClick?.(hit, { additiveKey });
      }, 280);
    };

    const onDblClick = event => {
      if (ctx.isDrawingActive?.()) {
        return;
      }

      clearTimeout(singleClickTimer);
      singleClickTimer = null;
      ignoreSingleClickUntil = Date.now() + 400;

      const pixel = map.getEventPixel(event.originalEvent);
      const hit = ctx.getMapClickTargetFeatureAtPixel?.(pixel);
      if (!hit) {
        ctx.onClearSelection?.();
        return;
      }
      ctx.onModuleDblClick?.(hit);
    };

    map.on('singleclick', onSingleClick);
    map.on('dblclick', onDblClick);

    return () => {
      clearTimeout(singleClickTimer);
      map.un('singleclick', onSingleClick);
      map.un('dblclick', onDblClick);
    };
  }
};
